import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Trash2, Plus, X } from 'lucide-react';
import { fmtDate } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_LINE = () => ({
  service_id: '',
  description: '',
  quantity: 1,
  unit_price: 0,
  iva_rate: '21',
  net: 0,
  iva_amount: 0,
  no_gravado: 0,
  exento: 0,
  total: 0,
});

const todayLocal = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const EMPTY_FORM = {
  insurer_id: '',
  document_type: 'FC',
  letra: 'A',
  punto_venta: '',
  numero: '',
  issue_date: '',  // Se setea en openNew() con la fecha LOCAL
  service_period_from: '',
  service_period_to: '',
  due_date: '',
  cae: '',
  cae_due_date: '',
  arca_environment: 'produccion',
  cbte_asoc_invoice_id: '',
  lines: [EMPTY_LINE()],
  notes: '',
};

const IVA_RATES = [
  { v: '21', label: '21%', pct: 0.21 },
  { v: '10.5', label: '10,5%', pct: 0.105 },
  { v: '0', label: '0%', pct: 0 },
  { v: 'exento', label: 'Exento', pct: 0 },
  { v: 'no_gravado', label: 'No gravado', pct: 0 },
];

const money = (v) => (Number(v) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// v3.43.0 · Panel de talonarios de facturación externa (ARCA Portal) a convenios.
// Fork del LegacyInvoiceImportPanel con asiento contable completo + libro IVA Ventas.
// v3.43.1 — Admite prop `embedded=true` para renderizarse como botón suelto
// (incrustado en la toolbar de /billing). Default `false` = card completa.
export function ExternalSalesPanel({ embedded = false, onCreated }) {
  const [rows, setRows] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [services, setServices] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [ncCandidates, setNcCandidates] = useState([]);

  const load = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/billing/external-sales`, { withCredentials: true });
      setRows(r.data.rows || []);
    } catch { setRows([]); }
  };
  const loadInsurers = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/health-insurers`, { withCredentials: true });
      setInsurers((r.data.rows || r.data || []).filter(i => i.is_active !== false));
    } catch { setInsurers([]); }
  };
  const loadServices = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/resident-services`, { withCredentials: true });
      setServices((r.data.rows || r.data || []).filter(s => s.is_active !== false));
    } catch { setServices([]); }
  };
  const loadNcCandidates = async (insurerId) => {
    if (!insurerId) { setNcCandidates([]); return; }
    try {
      const r = await axios.get(`${BACKEND_URL}/api/billing/external-sales/available-for-nc/${insurerId}`, { withCredentials: true });
      setNcCandidates(r.data.rows || []);
    } catch { setNcCandidates([]); }
  };

  useEffect(() => { if (!embedded) load(); loadInsurers(); loadServices(); }, [embedded]);

  useEffect(() => {
    if (form.document_type === 'NC' && form.insurer_id) loadNcCandidates(form.insurer_id);
    else setNcCandidates([]);
  }, [form.document_type, form.insurer_id]);

  const openNew = () => { setForm({ ...EMPTY_FORM, issue_date: todayLocal() }); setOpenForm(true); };
  const setF = (patch) => setForm(f => ({ ...f, ...patch }));

  // Zero-pad on blur
  const padBlur = (field, len) => () => setF({ [field]: String(form[field] || '').replace(/\D/g, '').padStart(len, '0').slice(-len) });

  // Línea helpers
  const setLine = (i, patch) => setForm(f => ({
    ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l),
  }));
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, EMPTY_LINE()] }));
  const removeLine = (i) => setForm(f => ({
    ...f, lines: f.lines.length > 1 ? f.lines.filter((_, idx) => idx !== i) : f.lines,
  }));

  // Recalcular una línea según iva_rate / quantity / unit_price
  const recalcLine = (i) => {
    setForm(f => {
      const ln = f.lines[i];
      const qty = Number(ln.quantity) || 0;
      const price = Number(ln.unit_price) || 0;
      const base = +(qty * price).toFixed(2);
      const rateDef = IVA_RATES.find(r => r.v === ln.iva_rate) || IVA_RATES[0];
      let net = 0, iva_amount = 0, no_gravado = 0, exento = 0, total = base;
      if (ln.iva_rate === 'no_gravado') { no_gravado = base; }
      else if (ln.iva_rate === 'exento') { exento = base; }
      else { net = base; iva_amount = +(base * rateDef.pct).toFixed(2); total = +(net + iva_amount).toFixed(2); }
      const newLines = f.lines.map((l, idx) => idx === i ? { ...l, net, iva_amount, no_gravado, exento, total } : l);
      return { ...f, lines: newLines };
    });
  };

  // Totales
  const totals = useMemo(() => {
    const t = { net: 0, iva: 0, ng: 0, ex: 0, total: 0 };
    for (const l of (form.lines || [])) {
      t.net += Number(l.net) || 0;
      t.iva += Number(l.iva_amount) || 0;
      t.ng += Number(l.no_gravado) || 0;
      t.ex += Number(l.exento) || 0;
      t.total += Number(l.total) || 0;
    }
    return t;
  }, [form.lines]);

  const canSave = useMemo(() => {
    if (!form.insurer_id || !form.punto_venta || !form.numero || !form.issue_date || !form.cae) return false;
    if (form.document_type === 'NC' && !form.cbte_asoc_invoice_id) return false;
    if (!form.lines.length) return false;
    for (const l of form.lines) {
      if (!l.service_id || Number(l.total) <= 0) return false;
    }
    return true;
  }, [form]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        cbte_asoc_invoice_id: form.document_type === 'NC' ? (form.cbte_asoc_invoice_id || null) : null,
        lines: form.lines.map(l => ({
          service_id: l.service_id,
          description: l.description || null,
          quantity: Number(l.quantity) || 1,
          unit_price: Number(l.unit_price) || 0,
          iva_rate: l.iva_rate,
          net: Number(l.net) || 0,
          iva_amount: Number(l.iva_amount) || 0,
          no_gravado: Number(l.no_gravado) || 0,
          exento: Number(l.exento) || 0,
          total: Number(l.total) || 0,
        })),
      };
      await axios.post(`${BACKEND_URL}/api/billing/external-sales`, payload, { withCredentials: true });
      toast.success(`${form.document_type} externa cargada: ${form.document_type}-${form.letra}-${form.punto_venta.padStart(5,'0')}-${form.numero.padStart(8,'0')}`);
      setOpenForm(false);
      setForm(EMPTY_FORM);
      if (!embedded) load();
      if (typeof onCreated === 'function') onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar la factura externa');
    } finally { setSaving(false); }
  };

  const serviceById = useMemo(() => Object.fromEntries(services.map(s => [s.service_id, s])), [services]);

  // v3.43.1 — Modo embedded: sólo botón + Dialog (sin card ni listado).
  const dialogJsx = (
    <Dialog open={openForm} onOpenChange={(v) => !saving && setOpenForm(v)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Nueva factura externa (ARCA Portal)</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-12 gap-3 text-sm">
            <div className="col-span-6">
              <Label>Convenio destinatario *</Label>
              <Select value={form.insurer_id} onValueChange={(v) => setF({ insurer_id: v, cbte_asoc_invoice_id: '' })}>
                <SelectTrigger data-testid="external-form-insurer"><SelectValue placeholder="Elegir convenio" /></SelectTrigger>
                <SelectContent>
                  {insurers.map(i => <SelectItem key={i.insurer_id} value={i.insurer_id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3">
              <Label>Tipo *</Label>
              <Select value={form.document_type} onValueChange={(v) => setF({ document_type: v, cbte_asoc_invoice_id: '' })}>
                <SelectTrigger data-testid="external-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FC">Factura</SelectItem>
                  <SelectItem value="ND">Nota de Débito</SelectItem>
                  <SelectItem value="NC">Nota de Crédito</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-3">
              <Label>Letra *</Label>
              <Select value={form.letra} onValueChange={(v) => setF({ letra: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="A">A</SelectItem>
                  <SelectItem value="B">B</SelectItem>
                  <SelectItem value="C">C</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.document_type === 'NC' && (
              <div className="col-span-12">
                <Label>Comprobante asociado (FC/ND origen) *</Label>
                <Select value={form.cbte_asoc_invoice_id} onValueChange={(v) => setF({ cbte_asoc_invoice_id: v })}>
                  <SelectTrigger data-testid="external-form-cbte-asoc">
                    <SelectValue placeholder={ncCandidates.length ? 'Elegir comprobante origen' : 'No hay FC/ND externas creditables del convenio'} />
                  </SelectTrigger>
                  <SelectContent>
                    {ncCandidates.map(c => (
                      <SelectItem key={c.invoice_id} value={c.invoice_id}>
                        {c.invoice_number} · {fmtDate(c.issue_date)} · creditable $ {money(c.creditable)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="col-span-3">
              <Label>Punto de venta *</Label>
              <Input value={form.punto_venta}
                     onChange={(e) => setF({ punto_venta: e.target.value.replace(/\D/g, '') })}
                     onBlur={padBlur('punto_venta', 5)}
                     maxLength={5}
                     placeholder="00001"
                     data-testid="external-form-pv" />
            </div>
            <div className="col-span-3">
              <Label>Número *</Label>
              <Input value={form.numero}
                     onChange={(e) => setF({ numero: e.target.value.replace(/\D/g, '') })}
                     onBlur={padBlur('numero', 8)}
                     maxLength={8}
                     placeholder="00000001"
                     data-testid="external-form-numero" />
            </div>
            <div className="col-span-3">
              <Label>Fecha de emisión *</Label>
              <Input type="date" value={form.issue_date} onChange={(e) => setF({ issue_date: e.target.value })} />
            </div>
            <div className="col-span-3">
              <Label>Vto. pago</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setF({ due_date: e.target.value })} />
            </div>

            <div className="col-span-6">
              <Label>CAE * (14 dígitos)</Label>
              <Input value={form.cae} onChange={(e) => setF({ cae: e.target.value.replace(/\D/g, '') })}
                     maxLength={14} placeholder="14 dígitos" data-testid="external-form-cae" />
            </div>
            <div className="col-span-3">
              <Label>Vto. CAE</Label>
              <Input type="date" value={form.cae_due_date} onChange={(e) => setF({ cae_due_date: e.target.value })} />
            </div>
            <div className="col-span-3">
              <Label>Ambiente</Label>
              <Select value={form.arca_environment} onValueChange={(v) => setF({ arca_environment: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="produccion">Producción</SelectItem>
                  <SelectItem value="homologacion">Homologación</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-4">
              <Label>Período desde</Label>
              <Input type="date" value={form.service_period_from} onChange={(e) => setF({ service_period_from: e.target.value })} />
            </div>
            <div className="col-span-4">
              <Label>Período hasta</Label>
              <Input type="date" value={form.service_period_to} onChange={(e) => setF({ service_period_to: e.target.value })} />
            </div>

            {/* ─── Líneas ─── */}
            <div className="col-span-12 border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base">Líneas de la factura</Label>
                <Button size="sm" variant="outline" onClick={addLine} data-testid="external-form-addline">
                  <Plus className="w-3 h-3 mr-1" /> Agregar línea
                </Button>
              </div>
              <div className="space-y-2">
                {form.lines.map((ln, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 rounded-md border p-2 bg-muted/30" data-testid={`external-form-line-${i}`}>
                    <div className="col-span-4">
                      <Label className="text-xs">Servicio *</Label>
                      <Select value={ln.service_id} onValueChange={(v) => {
                        const svc = serviceById[v];
                        setLine(i, { service_id: v, description: ln.description || svc?.name || '' });
                      }}>
                        <SelectTrigger><SelectValue placeholder="Elegir servicio" /></SelectTrigger>
                        <SelectContent>
                          {services.map(s => <SelectItem key={s.service_id} value={s.service_id}>{s.code} — {s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Descripción</Label>
                      <Input value={ln.description} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="(usa nombre del servicio)" />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">Cant.</Label>
                      <Input type="number" step="0.01" value={ln.quantity}
                             onChange={(e) => setLine(i, { quantity: e.target.value })}
                             onBlur={() => recalcLine(i)} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Precio unit.</Label>
                      <Input type="number" step="0.01" value={ln.unit_price}
                             onChange={(e) => setLine(i, { unit_price: e.target.value })}
                             onBlur={() => recalcLine(i)} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Alícuota IVA</Label>
                      <Select value={ln.iva_rate} onValueChange={(v) => { setLine(i, { iva_rate: v }); setTimeout(() => recalcLine(i), 0); }}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {IVA_RATES.map(r => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="col-span-2">
                      <Label className="text-xs">Neto gravado</Label>
                      <Input type="number" step="0.01" value={ln.net}
                             onChange={(e) => setLine(i, { net: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">IVA</Label>
                      <Input type="number" step="0.01" value={ln.iva_amount}
                             onChange={(e) => setLine(i, { iva_amount: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">No gravado</Label>
                      <Input type="number" step="0.01" value={ln.no_gravado}
                             onChange={(e) => setLine(i, { no_gravado: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Exento</Label>
                      <Input type="number" step="0.01" value={ln.exento}
                             onChange={(e) => setLine(i, { exento: e.target.value })} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Total línea</Label>
                      <Input type="number" step="0.01" value={ln.total}
                             onChange={(e) => setLine(i, { total: e.target.value })} />
                    </div>
                    <div className="col-span-1 flex items-end justify-end">
                      <Button size="icon" variant="ghost" onClick={() => removeLine(i)}
                              disabled={form.lines.length === 1}
                              data-testid={`external-form-removeline-${i}`}>
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totales */}
            <div className="col-span-12 border-t pt-3">
              <div className="grid grid-cols-5 gap-3 text-sm">
                <div><Label className="text-xs">Neto gravado</Label><div className="h-9 flex items-center font-medium">$ {money(totals.net)}</div></div>
                <div><Label className="text-xs">IVA</Label><div className="h-9 flex items-center font-medium">$ {money(totals.iva)}</div></div>
                <div><Label className="text-xs">No gravado</Label><div className="h-9 flex items-center font-medium">$ {money(totals.ng)}</div></div>
                <div><Label className="text-xs">Exento</Label><div className="h-9 flex items-center font-medium">$ {money(totals.ex)}</div></div>
                <div><Label className="text-xs">TOTAL</Label><div className="h-9 flex items-center text-base font-bold" data-testid="external-form-total">$ {money(totals.total)}</div></div>
              </div>
            </div>

            <div className="col-span-12">
              <Label>Notas (opcional)</Label>
              <Textarea value={form.notes || ''} onChange={(e) => setF({ notes: e.target.value })} rows={2} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenForm(false)} disabled={saving}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !canSave} data-testid="external-form-save">
              {saving ? 'Guardando...' : 'Guardar factura externa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );

  // v3.43.1 — Modo embedded = sólo botón visible (p. ej. toolbar de /billing)
  if (embedded) {
    return (
      <>
        <Button variant="outline" onClick={openNew}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="external-sales-new-btn">
          <Plus className="w-4 h-4 mr-2" /> FC externa (ARCA)
        </Button>
        {dialogJsx}
      </>
    );
  }

  // Modo panel completo (card con listado + botón)
  return (
    <Card data-testid="external-sales-panel">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base">Talonarios Facturas Externas (ARCA Portal) · solo CONVENIOS</CardTitle>
        <Button size="sm" onClick={openNew} data-testid="external-sales-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Nueva FC / ND / NC externa
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground text-xs">
          FC/ND/NC emitidas por fuera de la plataforma (ARCA Portal) a <b>convenios</b>. Al cargarse, integran el Libro IVA Ventas, generan asiento contable completo (Deudores OS / Ingreso / IVA DF) y mueven la cta.cte. del convenio. No disponible para facturación a residentes.
        </p>
        {rows.length === 0 ? (
          <p className="text-muted-foreground">Todavía no hay facturas externas cargadas.</p>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Comprobante</TableHead>
                  <TableHead>Convenio</TableHead>
                  <TableHead>CAE</TableHead>
                  <TableHead className="text-right">Neto</TableHead>
                  <TableHead className="text-right">IVA</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="external-sales-rows">
                {rows.map(r => (
                  <TableRow key={r.invoice_id}>
                    <TableCell>{fmtDate(r.issue_date)}</TableCell>
                    <TableCell className="font-mono text-xs">{r.invoice_number}</TableCell>
                    <TableCell>{(r.billed_to || {}).legal_name}</TableCell>
                    <TableCell className="font-mono text-xs">{r.cae}</TableCell>
                    <TableCell className="text-right">$ {money(r.subtotal_net)}</TableCell>
                    <TableCell className="text-right">$ {money(r.subtotal_iva)}</TableCell>
                    <TableCell className="text-right font-medium">$ {money(r.total)}</TableCell>
                    <TableCell className="capitalize">{(r.status || '').replace('_', ' ')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
      {dialogJsx}
    </Card>
  );
}
